
import { Component, Vue, Prop } from 'vue-property-decorator';
import { checkRole } from '@/modules/authentication/helpers/checkRole';
import { Roles } from '@/modules/authentication/models/rolesEnum';
import { generateCode128B } from '@/modules/analysis/helpers/generateBarcode128B';
import { Sample, SampleBatch } from '@/modules/analysis/models/analysisRequest';
import * as SampleBatchService from '@/modules/analysis/services/sampleBatchService';
import { htmlContent, sampleHTML } from '@/modules/analysis/components/display-request/printSamplesTemplate';
import SampleBatchDetails from '@/modules/analysis/components/SampleBatchDetails.vue';

@Component({
  components: { SampleBatchDetails }
})
export default class AboutSampleBatch extends Vue {
  @Prop()
  protected sampleBatches?: SampleBatch[];
  @Prop({ default: false })
  protected readonly!: boolean;
  @Prop({ default: null })
  protected laboratoryName!: string | null;
  protected dialog = false;
  protected stickerOffset = 0;
  protected samplePrintStart = 1;
  protected samplePrintCount = 50;

  /**
   * Open the print menu for barcode stickers
   */
  protected openPrintTab(sampleBatch: SampleBatch): void {
    if (!sampleBatch.id) {
      return;
    }
    SampleBatchService.read(sampleBatch.id).then((response) => {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';

      document.body.appendChild(iframe);

      if (iframe.contentWindow !== null) {
        const barcodesHTML = this.generateBarcodeHTML(
          { batchId: sampleBatch.id?.slice(0, 4) || 'xxxx', samples: response.data.samples },
          this.stickerOffsetInt,
          this.samplePrintStartInt,
          this.samplePrintCountInt
        );
        iframe.contentWindow.document.write(barcodesHTML);
        iframe.contentWindow.document.close();
      }

      iframe.onload = () => {
        if (iframe.contentWindow !== null) {
          iframe.contentWindow.print();
        }
        document.body.removeChild(iframe);
        this.stickerOffset = 0;
      };
    });
  }

  /**
   * Generate de html page to be printed. Defines the layout of the barcodes.
   */
  private generateBarcodeHTML(data: { batchId: string; samples: Sample[] }, offset = 0, start = 1, count = 50): string {
    let barcodeHTML = '';
    for (let i = 0; i < offset; i++) {
      barcodeHTML += `<div class="container"></div>`;
    }

    let idx = 1;
    for (const sample of data.samples) {
      if (idx < start) {
        idx++;
        continue;
      } else if (idx >= start + count) {
        break;
      }
      const formattedBarcode = generateCode128B(sample.barcode as string);
      barcodeHTML += sampleHTML
        .replace('<%formattedBarcode%>', formattedBarcode)
        .replace(
          '<%barcode%>',
          sample.barcode ? sample.barcode + '-' + data.batchId.toUpperCase() + (idx++).toString().padStart(2, '0') : ''
        )
        .replace('<%laboratoryName%>', this.laboratoryName || '')
        .replace('<%refLabel%>', this.$i18n.t('analysis.AboutSampleBatch.referenceLabel').toString())
        .replace('<%ref%>', sample.reference)
        .replace('<%samplingDateLabel%>', this.$i18n.t('analysis.AboutSampleBatch.samplingDateLabel').toString())
        .replace(
          '<%samplingDate%>',
          this.$options.filters ? this.$options.filters.dateFormat(new Date(sample.samplingDate), 'DD/MM/YY') : ''
        )
        .replace('<%size%>', sample.size)
        .replace('<%sizeUnit%>', sample.sizeUnit);
    }
    return htmlContent.replace('<%barcodesHTML%>', barcodeHTML);
  }

  protected get isSamplesStateUpdateAuthorized(): boolean {
    return checkRole(Roles.samples_update_state);
  }

  private get stickerOffsetInt(): number {
    return typeof this.stickerOffset === 'number' ? this.stickerOffset : parseInt(this.stickerOffset, 10);
  }
  private get samplePrintStartInt(): number {
    return typeof this.samplePrintStart === 'number' ? this.samplePrintStart : parseInt(this.samplePrintStart, 10);
  }
  private get samplePrintCountInt(): number {
    return typeof this.samplePrintCount === 'number' ? this.samplePrintCount : parseInt(this.samplePrintCount, 10);
  }
}
